/**
 * external-mcp.ts
 *
 * Runner-managed external MCP servers (stdio first; sse/http too).
 *
 * Historically external MCP servers declared in `skaile.yaml` (`mcp_servers:`,
 * including recipe-backed ones like excel / ppt) were handed to the claude-sdk
 * driver as `{type:"stdio"}` entries on the SDK's `mcpServers` query option, so
 * the **SDK** owned spawning the subprocess. That coupled external MCP support
 * to one driver (only claude-sdk consumes `mcpServers`) and to the
 * claude-agent-sdk version (external stdio servers via the query option require
 * the 0.3.x line).
 *
 * This module moves spawning + connection into the RUNNER: it spawns the MCP
 * subprocess itself via `@modelcontextprotocol/sdk`'s {@link Client} +
 * transport, runs `initialize` + `tools/list`, and registers each tool into the
 * v2 {@link CapabilityRegistry} as an `mcp`-origin capability named
 * `mcp__<server>__<tool>`. Invocations dispatch through the normal capability
 * path, so every driver (claude-sdk, omp, codex) reaches the tools through the
 * single surface they already consume — no dependence on the claude-agent-sdk
 * `mcpServers` option.
 *
 * Static imports only: the runner ships as `bun build --compile`, which cannot
 * follow dynamic imports. See `bridge/CLAUDE.md` § "Static-import-or-die".
 *
 * @docLink packages/runner/capabilities#runner-managed-external-mcp
 */
import { Client } from "@modelcontextprotocol/sdk/client/index.js";
import { SSEClientTransport } from "@modelcontextprotocol/sdk/client/sse.js";
import {
  getDefaultEnvironment,
  StdioClientTransport,
} from "@modelcontextprotocol/sdk/client/stdio.js";
import { StreamableHTTPClientTransport } from "@modelcontextprotocol/sdk/client/streamableHttp.js";
import type { Transport } from "@modelcontextprotocol/sdk/shared/transport.js";
import { isKnownSecretRef } from "@skaile/workspaces/connectors";
import { loadMcpServerDeclarations, type McpServerDeclaration } from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import type { ZodTypeAny } from "zod";
import type { CapabilityRegistry } from "./capability-registry.js";
import type { DefinedCapability } from "./define-capability.js";
import { createRecipeCache, resolveRecipePath } from "./recipe-resolver.js";
import {
  substituteRecipeMap,
  substituteRecipeTemplating,
  substituteWorkspaceToken,
  substituteWorkspaceTokenMap,
  validateSubstitutedPaths,
} from "./recipe-templating.js";

// ---------------------------------------------------------------------------
// Typed error for duplicate MCP server registration
// ---------------------------------------------------------------------------

/** Thrown by {@link ExternalMcpManager.addServer} when the id is already registered. */
export class McpServerAlreadyRegisteredError extends Error {
  constructor(public readonly id: string) {
    super(`MCP server '${id}' is already registered`);
    this.name = "McpServerAlreadyRegisteredError";
  }
}

// ---------------------------------------------------------------------------
// Secret resolution (shared with resources.ts' legacy SDK-delegation path)
// ---------------------------------------------------------------------------

/**
 * Minimal interface for secret resolution — keeps callers and tests free to
 * pass any `{ resolve }` object without constructing a real SecretProvider.
 */
export interface SecretProviderLike {
  resolve(ref: string): string | undefined;
}

/**
 * Resolve secret references (`env:VAR`, `forge:KEY`, etc.) in a string-keyed
 * record. Returns a new record with resolved values; entries that fail to
 * resolve are passed through unchanged.
 */
export function resolveRecordSecrets(
  record: Record<string, string>,
  secrets: SecretProviderLike | undefined,
): Record<string, string> {
  if (!secrets) return record;
  const resolved: Record<string, string> = {};
  for (const [k, v] of Object.entries(record)) {
    resolved[k] = secrets.resolve(v) ?? v;
  }
  return resolved;
}

/**
 * Strict variant of {@link resolveRecordSecrets} for transport construction: a
 * value that starts with a known secret-ref prefix (`env:`, `mcp:`, ...) but
 * fails to resolve is a hard error. Forwarding the literal ref would put it on
 * the wire (e.g. `Authorization: mcp:github`) and surface a misleading upstream
 * rejection instead of the real cause — the credential is unavailable.
 * Resolved refs and non-ref literals behave exactly like the lenient variant.
 */
export function resolveRecordSecretsStrict(
  record: Record<string, string>,
  secrets: SecretProviderLike | undefined,
  ctx: { serverId: string; kind: "header" | "env" },
): Record<string, string> {
  const resolved: Record<string, string> = {};
  for (const [k, v] of Object.entries(record)) {
    const value = secrets?.resolve(v);
    if (value === undefined && isKnownSecretRef(v)) {
      throw new Error(
        `credential for MCP server '${ctx.serverId}' unavailable — unresolved secret ref '${v}' in ${ctx.kind} '${k}'`,
      );
    }
    resolved[k] = value ?? v;
  }
  return resolved;
}

// ---------------------------------------------------------------------------
// Declaration resolution (recipe substitution → ready-to-spawn declarations)
// ---------------------------------------------------------------------------

/** IDs reserved for built-in MCP servers. Declarative servers must not shadow these. */
const RESERVED_MCP_SERVER_IDS = new Set([
  "skaile-connectors",
  "skaile-workspace",
  "skaile-capabilities",
]);

/**
 * Load the declarative MCP servers from `skaile.yaml` and resolve any
 * `recipe:` bindings into absolute `/nix/store` paths, substituting
 * `${recipe:<id>...}` markers in `command`, `args`, and `env`.
 *
 * Mirrors the resilience of the original wiring: a single broken declaration
 * (reserved id, recipe resolution failure, unresolved/invalid substituted
 * path) is logged and dropped — it never aborts the caller. Secret references
 * in `env` / `headers` are NOT resolved here; the spawn path resolves them at
 * transport-construction time so resolved secrets never sit in the returned
 * declaration objects.
 *
 * @returns The recipe-substituted declarations ready for spawn (or SDK
 *   delegation in the legacy fallback path).
 */
export async function resolveExternalMcpDeclarations(
  projectDir: string,
  sessionId?: string,
  managedCodex = false,
): Promise<McpServerDeclaration[]> {
  const mcpLog = createLogger({
    kind: "mcp",
    subkind: "wiring",
    instance: sessionId ?? "no-session",
  });

  const mcpDeclarations = await loadMcpServerDeclarations(projectDir);
  if (managedCodex && mcpDeclarations.length > 0)
    throw new Error("CODEX_MANAGED_PROJECT_MCP_UNSUPPORTED");
  mcpLog.debug("loaded MCP declarations", { count: mcpDeclarations.length });

  // One cache per pass — dedupes nix path-info shell-outs for multiple
  // declarations that share the same flake attr.
  const recipeCache = createRecipeCache();
  const nixFlakeRef = process.env.SKAILE_NIX_FLAKE_REF ?? "/etc/skaile/flake";

  const resolved: McpServerDeclaration[] = [];

  for (const decl of mcpDeclarations) {
    if (RESERVED_MCP_SERVER_IDS.has(decl.id)) {
      mcpLog.warn("MCP server skipped: reserved id", { id: decl.id });
      continue;
    }

    // An explicit launcher wins over a stale `recipe:` binding: `package-runner`
    // / `path` always pass through as command-shapes, even if a leftover
    // `recipe:` block is present. Only `launcher: recipe` (or an unset launcher
    // with a recipe binding — the back-compat default) triggers resolution.
    const wantsRecipe =
      decl.launcher === "recipe" || (decl.launcher == null && decl.recipe != null);
    if (!wantsRecipe) {
      resolved.push(decl); // package-runner / path / remote: command-shape pass-through
      continue;
    }
    if (!decl.recipe) {
      mcpLog.warn("MCP server skipped: launcher 'recipe' without a recipe binding", {
        id: decl.id,
      });
      continue;
    }

    const resolvedDecl = await resolveRecipeDeclaration(decl, nixFlakeRef, recipeCache);
    // `undefined` signals a logged skip (reserved-id-style "drop and continue").
    if (resolvedDecl) resolved.push(resolvedDecl);
  }

  // `${workspace}` → workspace root, applied to EVERY decl's command/args/env
  // (recipe + command-shape alike). url/headers are remote-transport config,
  // intentionally excluded.
  return resolved.map((decl) => ({
    ...decl,
    command: decl.command ? substituteWorkspaceToken(decl.command, projectDir) : decl.command,
    args: decl.args?.map((a) => substituteWorkspaceToken(a, projectDir)),
    env: substituteWorkspaceTokenMap(decl.env, projectDir),
  }));
}

/**
 * Resolve one declaration's `recipe:` binding into absolute `/nix/store` paths,
 * substituting `${recipe:<id>...}` markers in `command`, `args`, and `env`.
 *
 * Returns the substituted declaration, or `undefined` when the declaration must
 * be dropped — a substituted path that does not exist, or any thrown error
 * during resolution. The drop is logged here (mirroring the original inline
 * behavior) so the caller can simply skip a `undefined` result.
 *
 * Pre: `decl.recipe` is defined.
 */
async function resolveRecipeDeclaration(
  decl: McpServerDeclaration,
  nixFlakeRef: string,
  recipeCache: ReturnType<typeof createRecipeCache>,
): Promise<McpServerDeclaration | undefined> {
  // biome-ignore lint/style/noNonNullAssertion: caller guarantees decl.recipe is defined.
  const recipe = decl.recipe!;
  // For BYO-flake recipes `attr` is the in-flake target ("default") and may
  // be absent; `flake` is the meaningful log key. Hoisted above the try so
  // the catch can log it too.
  const recipeLogKey = recipe.flake ? { flake: recipe.flake } : { attr: recipe.attr };
  const recipeLog = createLogger({ kind: "mcp", subkind: decl.id, instance: "recipe-resolve" });
  try {
    // Legacy platform-flake recipes resolve by their `attr` (e.g.
    // `mcps.excel`); BYO-flake recipes all build `#default`, so resolve by
    // `mcps.<id>` — the key the host-side builder writes into the map.
    const recipeKey = recipe.flake ? `mcps.${decl.id}` : (recipe.attr ?? `mcps.${decl.id}`);
    const outPath = await resolveRecipePath(nixFlakeRef, recipeKey, recipeCache);
    const outPaths: ReadonlyMap<string, string> = new Map([[decl.id, outPath]]);

    const resolvedCommand = decl.command
      ? substituteRecipeTemplating(decl.command, outPaths)
      : decl.command;
    const resolvedArgs = decl.args?.map((a) => substituteRecipeTemplating(a, outPaths));
    const resolvedEnv = substituteRecipeMap(decl.env, outPaths);

    const resolvedDecl: McpServerDeclaration = {
      ...decl,
      command: resolvedCommand,
      args: resolvedArgs,
      env: resolvedEnv,
    };

    warnUnresolvedRecipeMarkers(recipeLog, decl.id, resolvedCommand, resolvedArgs, resolvedEnv);

    // Verify substituted /nix/store paths exist (catches recipes without bin/).
    const missingPaths = validateSubstitutedPaths(resolvedCommand, resolvedArgs);
    if (missingPaths.length > 0) {
      recipeLog.error("MCP server skipped: substituted path does not exist", undefined, {
        id: decl.id,
        ...recipeLogKey,
        missing: missingPaths,
      });
      return undefined;
    }

    recipeLog.info("recipe substitution complete", { ...recipeLogKey, outPath });
    return resolvedDecl;
  } catch (err) {
    // Catch ALL errors — a single broken declaration cannot abort resolution
    // for the whole session. Log the full error and skip this declaration.
    recipeLog.error("MCP server skipped: recipe resolution failed", err, {
      id: decl.id,
      ...recipeLogKey,
      flakeRef: nixFlakeRef,
    });
    return undefined;
  }
}

/** Warn on any `${recipe:...}` markers that survived substitution (id mismatch). */
function warnUnresolvedRecipeMarkers(
  recipeLog: ReturnType<typeof createLogger>,
  declId: string,
  resolvedCommand: string | undefined,
  resolvedArgs: string[] | undefined,
  resolvedEnv: Record<string, string> | undefined,
): void {
  const allResolved = [
    resolvedCommand ?? "",
    ...(resolvedArgs ?? []),
    ...Object.values(resolvedEnv ?? {}),
  ];
  for (const v of allResolved) {
    if (v.includes("${recipe:")) {
      recipeLog.warn("unresolved recipe marker after substitution", {
        value: v,
        declId,
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Runner-managed spawn + capability registration
// ---------------------------------------------------------------------------

// Passthrough sentinel (mirrors capability-registry's HYDRATED_PASSTHROUGH_ZOD):
// MCP tools advertise their own JSON Schema, registered verbatim as the wire
// `inputSchema`; the MCP server does the real validation. Avoids a runtime zod dep.
const PASSTHROUGH_INPUT_ZOD: ZodTypeAny = {
  parse: (v: unknown) => v,
} as unknown as ZodTypeAny;

/** A single tool advertised by an MCP server's `tools/list`. */
export interface McpToolDescriptor {
  name: string;
  description?: string;
  inputSchema?: unknown;
}

/**
 * Minimal MCP client surface the manager depends on. The real
 * `@modelcontextprotocol/sdk` {@link Client} satisfies this structurally; tests
 * inject a fake via the {@link ExternalMcpManager} constructor's `connect`
 * argument so they need no live subprocess and run under both Bun and Node.
 */
export interface McpClientLike {
  listTools(): Promise<{ tools: McpToolDescriptor[] }>;
  callTool(
    params: {
      name: string;
      arguments?: Record<string, unknown>;
    },
    resultSchema?: undefined,
    options?: { signal?: AbortSignal },
  ): Promise<{ content?: Array<{ type?: string; text?: string }>; isError?: boolean }>;
  close(): Promise<void>;
}

/** Connects (spawns/handshakes) the MCP client for one declaration. */
export type McpClientConnector = (decl: McpServerDeclaration) => Promise<McpClientLike>;

/**
 * Re-mints the backend-issued bearer for one `auth: backend` MCP server and
 * rewrites its provisioned `MCP__<id>__AUTH` secret in place. Returns `true`
 * when a fresh token was provisioned (the manager then rebuilds the transport
 * and reconnects), `false` when re-mint was impossible (the manager gives up
 * and propagates the original 401). Injected by the runner; absent in CLI mode.
 */
export type McpReauthorizer = (decl: McpServerDeclaration) => Promise<boolean>;

/**
 * True when an MCP **transport** error looks like an auth rejection. Remote
 * transports surface a 401 either as a `code: 401` (or `status: 401`) on the
 * thrown error or as a message carrying the HTTP `401` / a `WWW-Authenticate`
 * OAuth error code (`invalid_token` / `invalid_grant`). The bare word
 * "unauthorized" is intentionally NOT matched on its own — a tool's
 * business-logic error ("user unauthorized to access document") must not
 * trigger a pointless re-mint; real transport 401s carry the numeric status.
 */
export function isMcpAuthError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const code =
    (err as { code?: unknown; status?: unknown }).code ?? (err as { status?: unknown }).status;
  if (code === 401 || code === "401") return true;
  const message = (err as { message?: unknown }).message;
  if (typeof message !== "string") return false;
  return /\b401\b|invalid[_ ]token|invalid[_ ]grant/i.test(message);
}

/** Normalize an unknown thrown value to a log-safe message string. */
function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Build the transport for a declaration. For stdio the subprocess env is a safe
 * base (`getDefaultEnvironment()` — PATH/HOME/TMPDIR) merged under the asset's
 * declared env: the SDK's StdioClientTransport uses ONLY the passed env when
 * set, so without the base the child loses PATH. See the 2026-06-04 devlog note.
 */
function buildTransport(
  decl: McpServerDeclaration,
  secrets: SecretProviderLike | undefined,
): Transport {
  const transport = decl.transport ?? "stdio";

  if (transport === "stdio") {
    if (!decl.command) {
      throw new Error(`stdio MCP server '${decl.id}' is missing 'command'`);
    }
    const env = {
      ...getDefaultEnvironment(),
      ...resolveRecordSecretsStrict(decl.env ?? {}, secrets, { serverId: decl.id, kind: "env" }),
    };
    return new StdioClientTransport({
      command: decl.command,
      args: decl.args ?? [],
      env,
      stderr: "pipe",
    });
  }

  if (transport === "sse") {
    if (!decl.url) throw new Error(`sse MCP server '${decl.id}' is missing 'url'`);
    const headers = resolveRecordSecretsStrict(decl.headers ?? {}, secrets, {
      serverId: decl.id,
      kind: "header",
    });
    return new SSEClientTransport(new URL(decl.url), { requestInit: { headers } });
  }

  if (transport === "http") {
    if (!decl.url) throw new Error(`http MCP server '${decl.id}' is missing 'url'`);
    const headers = resolveRecordSecretsStrict(decl.headers ?? {}, secrets, {
      serverId: decl.id,
      kind: "header",
    });
    return new StreamableHTTPClientTransport(new URL(decl.url), { requestInit: { headers } });
  }

  throw new Error(`Unknown MCP transport '${transport}' for server '${decl.id}'`);
}

/**
 * Default connector: build the transport, construct the real SDK {@link Client},
 * and connect it (which spawns the stdio subprocess and runs `initialize`).
 */
function defaultMcpClientConnector(secrets: SecretProviderLike | undefined): McpClientConnector {
  return async (decl) => {
    const transport = buildTransport(decl, secrets);
    const client = new Client({ name: "skaile-runner", version: "1.0.0" }, { capabilities: {} });
    await client.connect(transport);
    return client as unknown as McpClientLike;
  };
}

interface RunningMcpServer {
  id: string;
  /** The declaration — needed to rebuild the transport on a 401 reconnect. */
  decl: McpServerDeclaration;
  /** Live client. Swapped in place by a 401 reconnect; tool handlers read it lazily. */
  client: McpClientLike;
  /** Capability names registered for this server — deregistered on dispose. */
  toolNames: string[];
  /** In-flight re-mint+reconnect, shared by concurrent 401s for this server. */
  reauthInFlight?: Promise<boolean>;
}

/**
 * Spawns and owns the lifecycle of every external MCP server for a session,
 * registering each server's tools into the {@link CapabilityRegistry}.
 *
 * One instance per session (one per `buildAgentResources` pass). Spawn after
 * recipe resolution; the subprocesses stay alive for the session and are killed
 * by {@link dispose} at session end. A spawn / connect failure for one server is
 * logged and skipped — it never aborts session startup (mirrors the
 * recipe-resolution "drop the server, continue" behavior).
 *
 * @category Runtime
 * @since 3.5.0
 */
export class ExternalMcpManager {
  private readonly servers: RunningMcpServer[] = [];
  private readonly log;
  private readonly connect: McpClientConnector;
  private readonly reauthorize?: McpReauthorizer;

  /**
   * @param registry  Session capability registry to register MCP tools into.
   * @param secrets   Secret provider for resolving `env:` / `forge:` refs in the
   *                  stdio subprocess env and sse/http headers.
   * @param sessionId Used for the logger instance slice.
   * @param connect   Optional connector override (tests inject a fake client so
   *                  no live subprocess is spawned). Defaults to the real SDK
   *                  client + transport.
   * @param reauthorize Optional re-mint callback for `auth: backend` remote
   *                  servers. When present, a 401 from a tool call (or the
   *                  initial connect) triggers a re-mint + reconnect.
   */
  constructor(
    private readonly registry: CapabilityRegistry,
    secrets: SecretProviderLike | undefined,
    sessionId?: string,
    connect?: McpClientConnector,
    reauthorize?: McpReauthorizer,
  ) {
    this.connect = connect ?? defaultMcpClientConnector(secrets);
    this.reauthorize = reauthorize;
    this.log = createLogger({
      kind: "mcp",
      subkind: "runner-managed",
      instance: sessionId ?? "no-session",
    });
  }

  /** True when at least one external server connected and registered tools. */
  hasServers(): boolean {
    return this.servers.length > 0;
  }

  /**
   * Connect every declaration and register its tools. Per-server failures are
   * logged and skipped so a single bad server never blocks the session.
   */
  async start(declarations: McpServerDeclaration[]): Promise<void> {
    for (const decl of declarations) {
      try {
        await this.addServer(decl);
      } catch (err) {
        // Never abort the session on a single broken server.
        this.log.error("MCP server spawn failed; skipping", err, {
          id: decl.id,
          transport: decl.transport ?? "stdio",
        });
      }
    }
  }

  /**
   * Connect one MCP server and register its tools into the capability registry.
   * Returns the server id and the registered tool capability names.
   * Throws if the id is already registered or if connect/listTools fails.
   */
  async addServer(decl: McpServerDeclaration): Promise<{ id: string; toolNames: string[] }> {
    if (this.servers.some((s) => s.id === decl.id)) {
      throw new McpServerAlreadyRegisteredError(decl.id);
    }

    const client = await this.connectWithReauth(decl);

    // Register the server record first so tool handlers read the LIVE client
    // (a 401 reconnect swaps server.client under the same capability).
    const server: RunningMcpServer = { id: decl.id, decl, client, toolNames: [] };
    const toolNames: string[] = [];
    try {
      const { tools } = await client.listTools();
      for (const tool of tools) {
        const cap = this.buildToolCapability(server, tool);
        this.registry.register(cap, "agent");
        toolNames.push(cap.name);
      }
    } catch (err) {
      // Connected but failed to enumerate/register — roll back any tools already
      // registered (they aren't tracked in `this.servers`, so dispose can't reach
      // them) and close the client so we don't leak the subprocess.
      for (const name of toolNames) this.registry.deregister(name);
      try {
        await client.close();
      } catch {
        /* best-effort */
      }
      throw err;
    }

    server.toolNames = toolNames;
    this.servers.push(server);
    this.log.info("MCP server registered (runner-managed)", {
      id: decl.id,
      transport: decl.transport ?? "stdio",
      toolCount: toolNames.length,
      tools: toolNames,
    });

    return { id: decl.id, toolNames };
  }

  /**
   * Deregister a running MCP server's tools and close its client.
   * No-op when the id is not found.
   *
   * Drains any in-flight reauth first: a doReauthAndReconnect resolving after
   * splice would swap server.client to a freshly-connected client that nothing
   * then closes, leaking the transport/subprocess.
   */
  async removeServer(id: string): Promise<void> {
    const idx = this.servers.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const server = this.servers[idx];
    // Drain the in-flight reauth before deregistering so we close the LATEST client.
    if (server.reauthInFlight) await server.reauthInFlight.catch(() => {});
    this.servers.splice(idx, 1);
    for (const name of server.toolNames) {
      this.registry.deregister(name);
    }
    try {
      await server.client.close();
    } catch {
      /* best-effort — close errors must not surface to the caller */
    }
    this.log.info("MCP server removed (runner-managed)", { id });
  }

  /** Summary of every currently-connected MCP server. */
  listServers(): Array<{ id: string; transport: "stdio" | "sse" | "http"; toolCount: number }> {
    return this.servers.map((s) => ({
      id: s.id,
      transport: (s.decl.transport ?? "stdio") as "stdio" | "sse" | "http",
      toolCount: s.toolNames.length,
    }));
  }

  /**
   * Connect, re-minting once on an initial-connect 401: the provisioned bearer
   * may already be stale by the time the server is reached. Only retries for
   * `auth: backend` servers with a reauthorizer wired.
   */
  private async connectWithReauth(decl: McpServerDeclaration): Promise<McpClientLike> {
    try {
      return await this.connect(decl);
    } catch (err) {
      if (isMcpAuthError(err) && this.reauthorize && decl.auth === "backend") {
        this.log.warn("MCP initial connect 401 — re-minting and retrying", { id: decl.id });
        if (await this.reauthorize(decl)) {
          return this.connect(decl);
        }
      }
      throw err;
    }
  }

  /**
   * Re-mint the bearer and reconnect one server in place after a 401. Concurrent
   * 401s for the same server share a single round-trip via `reauthInFlight`.
   * Returns `true` once `server.client` points at a freshly-authed connection.
   */
  private reauthAndReconnect(server: RunningMcpServer): Promise<boolean> {
    server.reauthInFlight ??= this.doReauthAndReconnect(server).finally(() => {
      server.reauthInFlight = undefined;
    });
    return server.reauthInFlight;
  }

  private async doReauthAndReconnect(server: RunningMcpServer): Promise<boolean> {
    if (!this.reauthorize || server.decl.auth !== "backend") return false;
    let provisioned: boolean;
    try {
      provisioned = await this.reauthorize(server.decl);
    } catch (err) {
      this.log.warn("MCP re-mint failed after 401", { id: server.id, error: errMessage(err) });
      return false;
    }
    if (!provisioned) return false;

    // The dead bearer is baked into the old transport's headers — only a fresh
    // client (rebuilt transport) picks up the rewritten MCP__<id>__AUTH secret.
    let next: McpClientLike;
    try {
      next = await this.connect(server.decl);
      await next.listTools(); // confirm the fresh token actually authenticates
    } catch (err) {
      this.log.warn("MCP reconnect after 401 failed", { id: server.id, error: errMessage(err) });
      return false;
    }
    const old = server.client;
    server.client = next;
    try {
      await old.close();
    } catch {
      /* best-effort — the old transport is being discarded */
    }
    this.log.info("MCP server reconnected after 401 re-mint", { id: server.id });
    return true;
  }

  /**
   * Wrap a single MCP tool as a {@link DefinedCapability}. The capability name
   * is `mcp__<server>__<tool>` (the naming claude-sdk wildcards and prompts
   * depend on). The handler proxies the call to the server's LIVE client (read
   * lazily so a 401 reconnect is transparent); a tool error (`isError`) is
   * rethrown so the capability dispatch path surfaces it to the LLM, and a 401
   * triggers a re-mint + reconnect followed by a single retry.
   */
  private buildToolCapability(
    server: RunningMcpServer,
    tool: McpToolDescriptor,
  ): DefinedCapability {
    const serverId = server.id;
    const name = `mcp__${serverId}__${tool.name}`;
    const inputSchema =
      tool.inputSchema && typeof tool.inputSchema === "object"
        ? (tool.inputSchema as Record<string, unknown>)
        : { type: "object" };

    return {
      name,
      description: tool.description ?? `MCP tool ${tool.name} from server ${serverId}`,
      inputSchema,
      side: "agent",
      origin: { kind: "mcp", serverId },
      scope: "session",
      kind: "effect",
      // LLM-only — these are agent tools, not user command-palette actions.
      audience: ["llm"],
      inputZod: PASSTHROUGH_INPUT_ZOD,
      handler: async (input, ctx) => {
        const call = { name: tool.name, arguments: (input ?? {}) as Record<string, unknown> };
        const invoke = () =>
          ctx.signal
            ? server.client.callTool(call, undefined, { signal: ctx.signal })
            : server.client.callTool(call);
        let result: { content?: Array<{ type?: string; text?: string }>; isError?: boolean };
        try {
          result = (await invoke()) as typeof result;
        } catch (err) {
          // A 401 mid-session means the provisioned bearer expired/was revoked.
          // Re-mint + reconnect once, then retry on the fresh client.
          if (
            !ctx.signal?.aborted &&
            isMcpAuthError(err) &&
            (await this.reauthAndReconnect(server))
          ) {
            ctx.log.info("mcp tool retried after 401 re-mint", { tool: name });
            result = (await invoke()) as typeof result;
          } else {
            throw err;
          }
        }

        const content = result.content ?? [];
        const text = content
          .filter((c) => c.type === "text" && typeof c.text === "string")
          .map((c) => c.text)
          .join("\n");

        if (result.isError) {
          throw new Error(text || `MCP tool ${name} returned an error`);
        }

        ctx.log.debug("mcp tool ok", { tool: name, hasText: text.length > 0 });
        // Prefer the concatenated text payload; fall back to the structured
        // content array for non-text results (images, resources, etc.).
        return text.length > 0 ? text : content;
      },
    };
  }

  /**
   * Deregister every tool and close every client (kills the subprocesses).
   * Best-effort by default: a failed close on one server does not prevent the rest.
   * With strict:true, report close failures after all owned cleanup attempts.
   *
   * Drains any in-flight 401 reconnect first: a `doReauthAndReconnect` resolving
   * after teardown would set `server.client` to a freshly-connected client that
   * nothing then closes (leak). Awaiting `reauthInFlight` guarantees we close the
   * LATEST client.
   */
  async dispose(options: { strict?: boolean } = {}): Promise<void> {
    const failures: unknown[] = [];
    await Promise.allSettled(this.servers.map((s) => s.reauthInFlight).filter(Boolean));
    for (const server of this.servers) {
      for (const toolName of server.toolNames) {
        this.registry.deregister(toolName);
      }
      try {
        await server.client.close();
      } catch (err) {
        failures.push(err);
        this.log.warn("MCP server close failed", {
          id: server.id,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    this.servers.length = 0;
    if (options.strict && failures.length > 0)
      throw new AggregateError(failures, "MCP server cleanup failed");
  }
}
